/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Nguyen Dat Huu Trong (ndhtrong1@rsupartners.com)
 *
 * @description
 *
 * @created at Fri Dec 27 2024
 **/

import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";

import { ChronoUtils, JotaiStore } from "@/frameworks/libraries";
import { SidebarID } from "@/frameworks/core/commons";
import { NotificationItem } from "./atom";
import { NotificationItem as MydeskNotificationItem } from "../useMydeskNotification/atom";
import useMeetingItem from "../useMeeting/useMeetingItem";
import { useMeeting } from "../../API";
import { usePersistUser } from "../../persistence";
import { AlarmLogMessageNormal } from "./types";
import { useSidebarActions, useSidebarState } from "../useSidebar";
import { IsMeetingOpenFromNoti, MeetingInfoNotiAtom } from "../../components";

export default function useNotificationMeeting() {
	const meetingAPI = useMeeting();
	const me = usePersistUser("me");
	const { openMeetingDetail, openPrivateMeetingDetail, openPendingMeetingInfo } = useMeetingItem({});
	const sidebarActions = useSidebarActions();
	const sidebarState = useSidebarState();
	const setOpenMeetingFromNoti = useSetAtom(IsMeetingOpenFromNoti);
	const setMeetingInfoNotiAtom = useSetAtom(MeetingInfoNotiAtom);

	const handleMeetingNoti = useAtomCallback(
		async (
			get,
			_,
			noti:
				| NotificationItem
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				| Message.Frame<Message.StompMessageTypes, any, Message.Options>
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				| MydeskNotificationItem<any>
		) => {
			try {
				switch (noti.type) {
					case AlarmLogMessageNormal.MEETING_SCHEDULE_REMIND:
					case AlarmLogMessageNormal.MEETING_SCHEDULE_INVITE: {
						setOpenMeetingFromNoti(true);

						if (!sidebarState.origin[SidebarID.Meeting]?.isOpen) {
							await sidebarActions.hideAll("left");
						}
						sidebarActions.setActive(SidebarID.Meeting, true);
						sidebarActions.show(SidebarID.Meeting);

						const payload = noti.payload;
						if (payload?.scheduleId && payload?.startTime) {
							const schedule = await meetingAPI.meeting.detail({
								scheduleId: payload?.scheduleId,
								scheduleStartTime: ChronoUtils.convert_date_with_timezone(payload?.startTime),
								timezone: me.timezone
							});

							if (schedule) {
								setMeetingInfoNotiAtom({
									scheduleId: schedule.scheduleId,
									scheduleStartTime: schedule.scheduleStartTime
								});
								if (schedule.privateMeeting) {
									openPrivateMeetingDetail(schedule);
								} else {
									openPendingMeetingInfo(schedule);
								}
							}
						}
						break;
					}
					case AlarmLogMessageNormal.MEETING_CONFERENCE_INVITE: {
						sidebarActions.show(SidebarID.Meeting);
						const conferenceId = noti.payload?.conferenceId || "";
						if (conferenceId) {
							const conference = await meetingAPI.meeting.detail({
								conferenceId,
								timezone: me.timezone
							});
							if (conference) {
								if (conference.privateMeeting) {
									openPrivateMeetingDetail(conference);
								} else {
									openMeetingDetail(conference);
								}
							}
						}
						break;
					}
					default:
						break;
				}
			} catch (err) {
				console.debug("#useNotificationMeeting - handle meeting noti Error", err);
			}
		},
		{ store: JotaiStore }
	);

	return {
		handleMeetingNoti
	};
}
